import dayjs from 'dayjs';

import { LanguageLevelEnum, LanguageType, LevelEnum } from '../../common/types';
import { MONTH_PICKER_DISPLAY_FORMAT, MONTH_PICKER_PAYLOAD_FORMAT } from '../../components/forms/FormMonthPicker';
import {
  CandidateAiScalarSuggestionField,
  CandidateAiScalarSuggestionType,
  CandidateAiSetSuggestionField,
  CandidateAiSetSuggestionType,
  CandidateType,
} from '../candidates/Candidates.types';

import { DisplaySetSuggestionGroup, DisplaySetSuggestionList, DisplaySuggestion } from './candidateAiSuggestions.types';
import { getCandidateAiSuggestionFieldLabel } from './useCandidateAiReviewActions';

export const formatCount = (count: number, noun: string, plural = `${noun}s`) =>
  `${count} ${count === 1 ? noun : plural}`;

export const formatSeniority = (value?: string | null) => {
  if (!value) return 'Not set';
  return LevelEnum[value as keyof typeof LevelEnum] ?? value;
};

export const formatProfessionalExperienceSince = (value?: string | null) => {
  if (!value || !/^\d{4}-\d{2}$/.test(value)) {
    return '-';
  }

  const parsedValue = dayjs(value, MONTH_PICKER_PAYLOAD_FORMAT);
  if (!parsedValue.isValid() || parsedValue.format(MONTH_PICKER_PAYLOAD_FORMAT) !== value) {
    return '-';
  }

  return parsedValue.format(MONTH_PICKER_DISPLAY_FORMAT);
};

export const formatStringValue = (value?: string | null) => value?.trim() || 'Not set';

export const formatLanguageValue = (value: LanguageType) => {
  const level = LanguageLevelEnum[value.level as keyof typeof LanguageLevelEnum] ?? value.level;
  return `${value.language} · ${level}`;
};

export const getCandidateDisplayValue = (candidate: CandidateType, field: CandidateAiScalarSuggestionField) => {
  switch (field) {
    case 'summary':
      return formatStringValue(candidate.summary);
    case 'professionalExperienceSince':
      return candidate.professionalExperienceSince
        ? formatProfessionalExperienceSince(candidate.professionalExperienceSince)
        : 'Not set';
    case 'seniority':
      return formatSeniority(candidate.seniority ? String(candidate.seniority) : null);
    case 'city':
      return formatStringValue(candidate.city);
    default:
      return 'Not set';
  }
};

export const getSuggestionDisplayValue = (
  field: CandidateAiScalarSuggestionField,
  suggestion?: CandidateAiScalarSuggestionType<string> | null
) => {
  if (!suggestion) return 'Not set';

  switch (field) {
    case 'summary':
    case 'city':
      return formatStringValue(String(suggestion.value));
    case 'seniority':
      return formatSeniority(String(suggestion.value));
    case 'professionalExperienceSince':
      return formatProfessionalExperienceSince(String(suggestion.value));
    default:
      return 'Not set';
  }
};

export const formatSetItemValue = (field: CandidateAiSetSuggestionField, value: string | LanguageType) => {
  if (field === 'languages') return formatLanguageValue(value as LanguageType);
  return String(value);
};

export const buildSuggestions = (candidate: CandidateType): DisplaySuggestion[] => {
  const suggestions = candidate.aiSuggestions;

  const definitions: Array<{
    field: CandidateAiScalarSuggestionField;
    section: 'General' | 'Additional data';
    suggestion?: CandidateAiScalarSuggestionType<string> | null;
  }> = [
    { field: 'summary', section: 'General', suggestion: suggestions.summary },
    { field: 'city', section: 'General', suggestion: suggestions.city },
    {
      field: 'professionalExperienceSince',
      section: 'Additional data',
      suggestion: suggestions.professionalExperienceSince,
    },
    { field: 'seniority', section: 'Additional data', suggestion: suggestions.seniority },
  ];

  return definitions.reduce<DisplaySuggestion[]>((acc, definition) => {
    if (!definition.suggestion) return acc;

    acc.push({
      field: definition.field,
      label: getCandidateAiSuggestionFieldLabel(definition.field),
      section: definition.section,
      status: definition.suggestion.status,
      suggestedValue: getSuggestionDisplayValue(definition.field, definition.suggestion),
      canonicalValue: getCandidateDisplayValue(candidate, definition.field),
    });

    return acc;
  }, []);
};

export const buildSetSuggestionGroups = (candidate: CandidateType): DisplaySetSuggestionGroup[] => {
  const definitions: Array<{
    field: CandidateAiSetSuggestionField;
    section: 'Additional data';
    suggestion?: CandidateAiSetSuggestionType<string | LanguageType> | null;
  }> = [
    { field: 'technologies', section: 'Additional data', suggestion: candidate.aiSuggestions.technologies },
    { field: 'languages', section: 'Additional data', suggestion: candidate.aiSuggestions.languages },
  ];

  return definitions.reduce<DisplaySetSuggestionGroup[]>((acc, definition) => {
    if (!definition.suggestion) return acc;

    const pendingItems = (definition.suggestion.pending ?? []).map((item) => ({
      value: item,
      displayValue: formatSetItemValue(definition.field, item),
    }));
    const rejectedItems = (definition.suggestion.rejected ?? []).map((item) => ({
      value: item,
      displayValue: formatSetItemValue(definition.field, item),
    }));

    if (!pendingItems.length && !rejectedItems.length) return acc;

    acc.push({
      field: definition.field,
      label: getCandidateAiSuggestionFieldLabel(definition.field),
      pendingItems,
      rejectedItems,
      section: definition.section,
    });

    return acc;
  }, []);
};

// Review works on pending items; rejected ones stay reachable but out of counts and bulk actions.
export const splitSuggestionsByStatus = (candidate: CandidateType) => {
  const rows = buildSuggestions(candidate);
  const groups = buildSetSuggestionGroups(candidate);
  const lists = (key: 'pendingItems' | 'rejectedItems'): DisplaySetSuggestionList[] =>
    groups
      .map((group) => ({ field: group.field, label: group.label, items: group[key] }))
      .filter((list) => list.items.length > 0);
  const pendingRows = rows.filter((row) => row.status === 'PENDING');
  const rejectedRows = rows.filter((row) => row.status === 'REJECTED');
  const pendingLists = lists('pendingItems');
  const rejectedLists = lists('rejectedItems');
  const countItems = (listsToCount: DisplaySetSuggestionList[]) =>
    listsToCount.reduce((total, list) => total + list.items.length, 0);

  return {
    pendingRows,
    pendingLists,
    rejectedRows,
    rejectedLists,
    pendingCount: pendingRows.length + countItems(pendingLists),
    rejectedCount: rejectedRows.length + countItems(rejectedLists),
  };
};
